import { Delete, Edit, PowerSettingsNew } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  DateField,
  Labeled,
  SimpleShowLayout,
  TextField,
  UpdateButton,
  useGetIdentity,
  useRecordContext,
  useRefresh,
} from 'react-admin';
import { Semaphore } from '../therapistAppConfig/Semaphore';
import { AppGlobalAlertEditDialog } from './AppGlobalAlertEditDialog';

export const AppGlobalAlertsCommandPanel = () => {
  const record = useRecordContext();
  const refresh = useRefresh();
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const userId = useGetIdentity().data?.id?.toString();

  const archiveData = useMemo(
    () => ({
      archivedAt: new Date(),
      archivedBy: userId,
      enabledAt: null,
    }),
    [userId],
  );

  const unarchiveData = useMemo(
    () => ({
      archivedAt: null,
      archivedBy: userId,
    }),
    [userId],
  );

  const enableData = useMemo(() => ({ enabledAt: new Date() }), []);
  const disableData = useMemo(() => ({ enabledAt: null }), []);

  const mutationOptions = useMemo(
    () => ({
      onSuccess: () => {
        refresh();
      },
    }),
    [refresh],
  );

  const isArchived = !!record?.archivedAt;
  const isEnabled = !!record?.enabledAt;

  return (
    <SimpleShowLayout record={record}>
      <TextField source="id" />
      <TextField source="singleLineDescription" />
      <TextField source="title" />
      <Labeled label="Status">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Semaphore
            enabled={isEnabled}
            enabledText=" Enabled"
            disabledText="Disabled"
          />
          {!isArchived && !isEnabled && (
            <UpdateButton
              resource="appGlobalAlerts"
              mutationMode="pessimistic"
              confirmTitle="Enable this alert?"
              confirmContent="This alert will become visible to all users."
              label="Enable"
              icon={<PowerSettingsNew />}
              data={enableData}
              mutationOptions={mutationOptions}
              color="error"
            />
          )}
          {isEnabled && <DateField source="enabledAt" showTime />}
          {!isArchived && isEnabled && (
            <UpdateButton
              resource="appGlobalAlerts"
              mutationMode="pessimistic"
              confirmTitle="Disable this alert?"
              confirmContent="This alert will no longer be visible to users."
              label="Disable"
              icon={<PowerSettingsNew />}
              data={disableData}
              mutationOptions={mutationOptions}
              color="success"
            />
          )}
          {isArchived && (
            <Typography variant="body2" color="text.secondary">
              Unarchive the Alert first to re-enable it.
            </Typography>
          )}
        </Box>
      </Labeled>
      <Labeled label="Content">
        <Box
          sx={{
            maxHeight: 320,
            overflow: 'auto',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            bgcolor: 'action.hover',
          }}
        >
          {record?.content ? (
            <Typography component="div" variant="body2">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {record.content}
              </ReactMarkdown>
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No content
            </Typography>
          )}
        </Box>
      </Labeled>
      {!record?.archivedAt && (
        <>
          <Button
            startIcon={<Edit />}
            onClick={() => setEditDialogOpen(true)}
          >
            Edit
          </Button>
          <AppGlobalAlertEditDialog
            open={editDialogOpen}
            onClose={() => setEditDialogOpen(false)}
            record={record ?? null}
            onSuccess={() => refresh()}
          />
        </>
      )}
      {!record?.archivedAt && (
        <UpdateButton
          resource="appGlobalAlerts"
          mutationMode="pessimistic"
          style={{ marginLeft: '1rem' }}
          confirmTitle="Are you sure?"
          confirmContent="This will archive this Alert and will no longer be visible to Users"
          label="Archive"
          icon={<Delete />}
          data={archiveData}
          mutationOptions={mutationOptions}
        />
      )}
      {record?.archivedAt && (
        <>
          <DateField source="archivedAt" showTime />
          <UpdateButton
            resource="appGlobalAlerts"
            style={{ marginLeft: '1rem' }}
            label="Unarchive"
            mutationMode="pessimistic"
            confirmTitle="Are you sure?"
            confirmContent="This will unarchive this Alert and become visible to all Users"
            data={unarchiveData}
            mutationOptions={mutationOptions}
          />
        </>
      )}
    </SimpleShowLayout>
  );
};
